import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Wraps a native `<dialog>` opened with `showModal()`.
 *
 * The native element is doing the accessibility work here rather than the
 * theme: `showModal` moves the dialog to the top layer, makes everything
 * behind it inert (so a screen reader and the Tab key both stop at the
 * dialog's edge), handles Escape, and returns focus to whatever opened it.
 * A hand-rolled focus trap gets all of that subtly wrong, and this element
 * exists only for the parts the platform still leaves open: locking the page
 * behind it from scrolling, closing on a backdrop click, and letting the
 * close animation finish before the element leaves the top layer.
 *
 *   <dialog-component id="cart-drawer">
 *     <dialog ref="dialog"> ... </dialog>
 *   </dialog-component>
 *
 *   <button on:click="#cart-drawer/open">Cart</button>
 *   <button on:click="dialog-component/close">Close</button>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @returns {HTMLDialogElement} */
  get dialog() {
    return /** @type {HTMLDialogElement} */ (this.refs.dialog);
  }

  get isOpen() {
    return this.dialog.open;
  }

  connectedCallback() {
    super.connectedCallback();
    // Marks this element for the scroll-lock check below, which has to find
    // every open dialog including subclasses using their own tag name.
    this.dataset.dialog = '';
    this.dialog.addEventListener('cancel', this.#onCancel);
    this.dialog.addEventListener('close', this.#afterClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.dialog.removeEventListener('cancel', this.#onCancel);
    this.dialog.removeEventListener('close', this.#afterClose);
    this.#unlockScroll();
  }

  open() {
    if (this.isOpen) return;

    this.dialog.showModal();
    this.#wasOpen = true;
    this.#lockScroll();
    this.setAttribute('open', '');
    this.#announceToggle(true);

    // Focus what the dialog nominates rather than the first tabbable node,
    // which for a drawer is the close button and tells the shopper nothing.
    const initial = this.querySelector('[autofocus]');
    if (initial instanceof HTMLElement) initial.focus();
  }

  /**
   * Opens from a link that also works without JavaScript. The href stays a
   * real destination (the cart page) so the link is meaningful when the
   * modules have not loaded; this only cancels the navigation once they have.
   *
   * A modified click is left alone: someone holding cmd or shift is asking for
   * a new tab or window, and a drawer is not what they asked for.
   *
   * @param {MouseEvent} event
   */
  openFrom(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    this.open();
  }

  async close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    this.setAttribute('closing', '');

    // `close()` yanks the dialog out of the top layer immediately, which would
    // make a slide-out animation invisible. Wait for it, but never forever.
    await Promise.race([
      Promise.allSettled(this.dialog.getAnimations().map((animation) => animation.finished)),
      new Promise((resolve) => setTimeout(resolve, 400)),
    ]);

    this.removeAttribute('closing');
    this.dialog.close();

    // Cleaned up here rather than only in the `close` listener. That listener
    // stays as the backstop for closes this method did not initiate, but the
    // page must not be left scroll-locked because one event failed to arrive.
    // Both paths are idempotent.
    this.#afterClose();
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Closes on a click that lands on the backdrop. The dialog element itself
   * fills the viewport as far as the event target is concerned, so the test is
   * whether the point falls outside the panel's box rather than what was hit.
   *
   * @param {MouseEvent} event
   */
  closeOnBackdrop(event) {
    const panel = this.querySelector('[data-dialog-panel]') ?? this.dialog;
    const { top, bottom, left, right } = panel.getBoundingClientRect();
    const outside =
      event.clientY < top || event.clientY > bottom || event.clientX < left || event.clientX > right;

    // A drag that started inside and ended outside is a text selection, not a
    // dismissal.
    if (outside && event.target === this.dialog) this.close();
  }

  /**
   * Escape. The browser would close the dialog outright, skipping the
   * animation the close button gets; cancelling and going through `close()`
   * makes both exits look the same.
   *
   * @param {Event} event
   */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #afterClose = () => {
    if (!this.hasAttribute('open') && !this.hasAttribute('closing') && !this.#wasOpen) return;

    this.#wasOpen = false;
    this.removeAttribute('open');
    this.removeAttribute('closing');
    this.#unlockScroll();
    this.#announceToggle(false);
  };

  /** Guards `#afterClose` against running twice for one close. */
  #wasOpen = false;

  /**
   * @param {boolean} open
   */
  #announceToggle(open) {
    this.dispatchEvent(
      new CustomEvent(ThemeEvents.dialogToggle, { bubbles: true, detail: { id: this.id, open } })
    );
  }

  #lockScroll() {
    // Compensating for the scrollbar keeps the page behind from shifting
    // sideways as it locks, which reads as the whole layout jumping.
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-gutter', `${gutter}px`);
    document.documentElement.setAttribute('data-scroll-locked', '');
  }

  #unlockScroll() {
    if (document.querySelector('[data-dialog][open]')) return;
    document.documentElement.removeAttribute('data-scroll-locked');
    document.documentElement.style.removeProperty('--scrollbar-gutter');
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}
